import { useEffect, useMemo, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { PlusIcon, Trash2Icon, XIcon } from 'lucide-react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { contactsAPI, contactsEndpoints } from '@/api/contacts';
import {
  SidebarWrapper,
  SidebarWrapperContent,
  SidebarWrapperFooter,
  SidebarWrapperForm,
  SidebarWrapperTitle,
} from '@/components/atoms/SidebarWrapper/SidebarWrapper';
import { UnsavedChangesModal } from '@/components/organisms/Modals/UnsavedChangesModal/UnsavedChangesModal';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useEscapeKey } from '@/hooks/useEscapeKey';
import { cn } from '@/lib/utils';
import type { Contact, ContactIdentity } from '@/shared/types/contacts';
import { createYupResolver } from '@/util/forms/createYupResolver';

import {
  buildContactFormSchema,
  cleanEmails,
  cleanPhones,
  type ContactFormValues,
  diffIdentities,
  type IdentityUpsertOp,
  isSnapshotEqual,
  MAX_EMAILS,
  MAX_PHONES,
  pickScalarRepoint,
  withCanonicalFirstValue,
  withCanonicalRowValue,
} from './contactForm';
import { useContactIdentitiesMutation } from '../hooks/useContactIdentitiesMutation';
import { useContacts } from '../hooks/useContacts';
import { useContactWildcardConsent } from '../hooks/useContactWildcardConsent';

const INVALID_INPUT = 'aria-invalid:border-destructive';

/** Trash affix rendered inside a text field (email / phone), matching the design. */
const InlineRemoveButton = ({ onClick, label }: { onClick: () => void; label: string }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    className="absolute top-1/2 right-2 -translate-y-1/2 rounded p-0.5 text-muted-foreground transition hover:bg-selected-soft hover:text-destructive"
  >
    <Trash2Icon size={16} />
  </button>
);

interface UpsertOutcome {
  conflicts: string[];
  otherFailures: number;
  results: PromiseSettledResult<void>[];
}

/** What a value we sent was actually stored as, once the server normalized it. */
interface RewrittenValue {
  sent: string;
  stored: string;
}

interface RepointOutcome {
  /** Whether a primary email/phone write was needed at all. */
  attempted: boolean;
  ok: boolean;
  rewritten?: { email?: RewrittenValue; phone?: RewrittenValue };
}

interface ContactFormPanelProps {
  mode: 'create' | 'edit';
  contact?: Contact;
  identities?: ContactIdentity[];
  onClose: () => void;
}

export const ContactFormPanel = ({
  mode,
  contact,
  identities = [],
  onClose,
}: ContactFormPanelProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { createContact } = useContacts();
  const { upsertIdentity, deleteIdentity } = useContactIdentitiesMutation();

  // Freeze the identities the panel opened with. The identities query can refresh
  // underneath an open panel (window-focus refetch, another actor's write), and
  // diffing the form against rows it never rendered would queue every
  // late-arriving identity for deletion.
  const initialIdentitiesRef = useRef(identities);
  const initialIdentities = initialIdentitiesRef.current;

  const { hasRevokedWildcard, status: wildcardStatus } = useContactWildcardConsent(
    mode === 'edit' ? contact?.contact_id : undefined,
    initialIdentities
  );

  const isEdit = mode === 'edit';
  const contactId = contact?.contact_id;

  // Consent must be resolved before an edit can be submitted. While it is
  // pending or failed the checkbox reads "opted in", so submitting would treat a
  // revoked contact as allowed and leave any identity added here messageable.
  const isConsentReady = !isEdit || wildcardStatus === 'success';

  // A contact can be reachable through a psid / external_id identity alone
  // (inbound social channels). Those have no row in this form, so the
  // email-or-phone rule must not lock such a contact out of a plain name edit.
  const hasUneditableIdentity = initialIdentities.some(
    (i) => (i.type === 'psid' || i.type === 'external_id') && !i.deleted_at
  );

  // A contact with no name is normal (inbound-created ones have none), and the
  // orchestrator's updateSchema lets the key be omitted — so those stay editable
  // without inventing a name. Create still needs one, and an existing name can't
  // be cleared, because the server rule underneath is `min(1)`.
  const requireName = !isEdit || Boolean(contact?.name?.trim());

  const schema = useMemo(() => buildContactFormSchema(t, requireName), [t, requireName]);

  // Computed once, for the panel's lifetime. This holds only because the caller
  // keys the panel by contact id: the route reuses one instance across a
  // `:contactId` change, so without that key these defaults (and the refs below)
  // would describe a different contact than the one being submitted.
  const defaultValues = useMemo<ContactFormValues>(() => {
    if (isEdit) {
      const emails = initialIdentities
        .filter((i) => i.type === 'email' && !i.deleted_at)
        .map((i) => ({ value: i.value, identityId: i.identity_id }));
      const phones = initialIdentities
        .filter((i) => i.type === 'phone' && !i.deleted_at)
        .map((i) => ({ value: i.value, identityId: i.identity_id }));
      return {
        name: contact?.name ?? '',
        emails: emails.length ? emails : [{ value: '' }],
        phones: phones.length ? phones : [{ value: '' }],
        consent: true,
      };
    }
    return { name: '', emails: [{ value: '' }], phones: [{ value: '' }], consent: true };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const form = useForm<ContactFormValues>({
    resolver: createYupResolver<ContactFormValues>(schema),
    defaultValues,
  });

  const emailsArray = useFieldArray({ control: form.control, name: 'emails' });
  const phonesArray = useFieldArray({ control: form.control, name: 'phones' });

  const [contactMethodError, setContactMethodError] = useState<string | null>(null);
  const [isUnsavedOpen, setIsUnsavedOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Baseline for the dirty guard; updated once the async consent query hydrates
  // the form (edit mode only).
  const snapshotRef = useRef<ContactFormValues>(defaultValues);
  const hydratedRef = useRef(false);

  // A create that reached the contact but failed on its identities or consent
  // revoke leaves the panel open to retry; the created contact is kept so the
  // retry resumes instead of creating a second one.
  const createdContactRef = useRef<Contact | null>(null);

  // In edit mode the wildcard-consent state loads asynchronously; fold it into
  // the form (and the dirty-guard baseline) once it loads, preserving any
  // early edits by reading current values. A failed load is never folded in:
  // "no revoked row seen" is not the same as "opted in".
  useEffect(() => {
    if (!isEdit || hydratedRef.current || wildcardStatus !== 'success') {
      return;
    }
    const consent = !hasRevokedWildcard;
    // Build the dirty-guard baseline from the ORIGINAL contact/identity props
    // (not the live form values). Edits typed before consent settled therefore
    // stay dirty — they are diffed and sent, instead of being folded into the
    // baseline and silently dropped.
    snapshotRef.current = { ...defaultValues, consent };
    // Reflect the loaded consent in the checkbox, unless the user already toggled it.
    if (!form.getFieldState('consent').isDirty) {
      form.setValue('consent', consent);
    }
    hydratedRef.current = true;
  }, [isEdit, wildcardStatus, hasRevokedWildcard, form, defaultValues]);

  // Clear the "email or phone required" message as soon as the user edits a
  // contact method (it is validated imperatively on submit, not via yup).
  useEffect(
    () =>
      form.subscribe({
        formState: { values: true },
        callback: ({ name }) => {
          if (contactMethodError && (name?.startsWith('emails') || name?.startsWith('phones'))) {
            setContactMethodError(null);
          }
        },
      }),
    [form, contactMethodError]
  );

  const isFormModified = () => !isSnapshotEqual(form.getValues(), snapshotRef.current);

  const handleClose = () => {
    // Closing mid-flight would unmount the panel while the create/update
    // requests are still running: they are not cancellable, so the write would
    // land anyway and the user would have "discarded" a change that happened.
    if (isSubmitting) {
      return;
    }
    if (isFormModified()) {
      setIsUnsavedOpen(true);
    } else {
      onClose();
    }
  };

  useEscapeKey(() => {
    if (!isUnsavedOpen) {
      handleClose();
    }
  });

  const handleAddEmail = () => {
    if (emailsArray.fields.length >= MAX_EMAILS) {
      toast.error(t('contacts.errors.max_emails_reached', { 0: MAX_EMAILS }));
      return;
    }
    emailsArray.append({ value: '' });
  };

  const handleRemoveEmail = (index: number) => {
    if (emailsArray.fields.length <= 1) {
      emailsArray.replace([{ value: '' }]);
    } else {
      emailsArray.remove(index);
    }
    setContactMethodError(null);
  };

  const handleAddPhone = () => {
    if (phonesArray.fields.length >= MAX_PHONES) {
      toast.error(t('contacts.errors.max_phones_reached', { 0: MAX_PHONES }));
      return;
    }
    phonesArray.append({ value: '' });
  };

  const handleRemovePhone = (index: number) => {
    if (phonesArray.fields.length <= 1) {
      phonesArray.replace([{ value: '' }]);
    } else {
      phonesArray.remove(index);
    }
    setContactMethodError(null);
  };

  const runUpserts = async (cId: string, ops: IdentityUpsertOp[]): Promise<UpsertOutcome> => {
    const results = await Promise.allSettled(
      ops.map((op) => upsertIdentity({ contactId: cId, type: op.type, value: op.value }))
    );
    const conflicts: string[] = [];
    let otherFailures = 0;
    results.forEach((result, index) => {
      if (result.status !== 'rejected') {
        return;
      }
      const reason = result.reason as { statusCode?: number } | undefined;
      if (reason?.statusCode === 409) {
        conflicts.push(ops[index].value);
      } else {
        otherFailures += 1;
      }
    });
    return { conflicts, otherFailures, results };
  };

  // Fan out a granted/revoked wildcard consent to every reachable identity.
  // Returns whether the whole operation (listing + every write) succeeded, so the
  // caller never reports success when a consent change was not actually applied.
  const writeWildcardConsent = async (
    cId: string,
    status: 'granted' | 'revoked'
  ): Promise<{ ok: boolean }> => {
    let list: ContactIdentity[];
    try {
      list = await contactsAPI.listIdentities(cId);
    } catch {
      return { ok: false };
    }
    const reachable = list.filter(
      (i) => (i.type === 'email' || i.type === 'phone') && !i.deleted_at
    );
    const results = await Promise.allSettled(
      reachable.map((i) => contactsAPI.setConsent(cId, i.identity_id, { status, source: 'manual' }))
    );
    reachable.forEach((i) =>
      queryClient.invalidateQueries({ queryKey: [contactsEndpoints.consents(cId, i.identity_id)] })
    );
    return { ok: results.every((r) => r.status === 'fulfilled') };
  };

  /** Surfaces every failure and reports whether the whole submit landed cleanly. */
  const reportOutcome = (
    outcome: UpsertOutcome,
    extra: { deleteFailures: number; consentOk: boolean; repointOk?: boolean }
  ): boolean => {
    if (outcome.conflicts.length > 0) {
      toast.error(
        t('contacts.errors.identity_cross_contact_conflict', { 0: outcome.conflicts.join(', ') })
      );
    }
    if (outcome.otherFailures > 0) {
      toast.error(t('contacts.errors.identities_partial_failure', { 0: outcome.otherFailures }));
    }
    if (extra.deleteFailures > 0) {
      toast.error(t('contacts.errors.identities_delete_failure', { 0: extra.deleteFailures }));
    }
    if (!extra.consentOk) {
      toast.error(t('contacts.errors.consent_update_failed'));
    }
    if (extra.repointOk === false) {
      // The identity rows may well have saved, but the contact's primary
      // email/phone did not, so the list keeps rendering the old value.
      toast.error(t('contacts.update_failed'));
    }
    return (
      outcome.conflicts.length === 0 &&
      outcome.otherFailures === 0 &&
      extra.deleteFailures === 0 &&
      extra.consentOk &&
      extra.repointOk !== false
    );
  };

  const submitCreate = async (values: ContactFormValues, emails: string[], phones: string[]) => {
    let created = createdContactRef.current;
    // A failed name write on the resume path must not abort the rest: the consent
    // revoke below is the reason the panel stayed open. Reported, not fatal.
    let contactWriteOk = true;

    if (created) {
      // Resuming after a partial failure. A name edited in the meantime would
      // otherwise be dropped, since the contact itself already exists. Only the
      // name is resent: email/phone go through the server's strict E.164 /
      // address validation, and a value it rejects must not be able to block the
      // opt-out retry. Their identity rows are still upserted below.
      const name = values.name.trim();
      if (name && name !== created.name?.trim()) {
        try {
          await contactsAPI.updateContact(created.contact_id, { name });
          created = { ...created, name };
          createdContactRef.current = created;
          queryClient.invalidateQueries({
            queryKey: [contactsEndpoints.contact(created.contact_id)],
          });
          queryClient.invalidateQueries({ queryKey: ['contacts/list'] });
        } catch {
          contactWriteOk = false;
        }
      }
    } else {
      const payload: Partial<Contact> = { name: values.name.trim() };
      if (emails[0]) {
        payload.email = emails[0];
      }
      if (phones[0]) {
        payload.phone = phones[0];
      }

      try {
        created = await createContact(payload);
      } catch (error) {
        toast.error(error instanceof Error ? error.message : t('contacts.create_failed'));
        return;
      }
      // Remembered so a retry resumes from here instead of creating a duplicate.
      createdContactRef.current = created;
    }

    // POST /contacts normalizes email/phone (lowercase / E.164) and indexes the
    // normalized value as an identity. Upsert what came back rather than the raw
    // text, or a differently-formatted first value ("+54 9 2494 621613") lands a
    // second identity row next to the normalized one ("+5492494621613").
    const identityEmails = withCanonicalFirstValue(emails, created.email);
    const identityPhones = withCanonicalFirstValue(phones, created.phone);

    const ops: IdentityUpsertOp[] = [
      ...identityEmails.map((value) => ({ type: 'email' as const, value })),
      ...identityPhones.map((value) => ({ type: 'phone' as const, value })),
    ];
    const outcome = await runUpserts(created.contact_id, ops);

    // Checked at submit writes nothing (implicit allow); unchecked revokes.
    let consentOk = true;
    if (!values.consent) {
      consentOk = (await writeWildcardConsent(created.contact_id, 'revoked')).ok;
    }

    // Stay open when anything failed. The contact exists and is messageable, so
    // an unapplied opt-out has to remain retryable: closing here would leave the
    // user no way back other than reopening the contact they just created.
    if (reportOutcome(outcome, { deleteFailures: 0, consentOk, repointOk: contactWriteOk })) {
      toast.success(t('contacts.created'));
      onClose();
    }
  };

  /**
   * Keep `contacts.email` / `contacts.phone` — the stored columns the contacts
   * list renders — pointing at the first email / phone in the form.
   *
   * Sent as its own request rather than folded into the name write above: the
   * server validates these columns as strict E.164, which is narrower than this
   * form accepts, so a phone it rejects must not take a rename down with it.
   * Returns the values the server echoed back, which are the normalized ones it
   * indexed as identities.
   */
  const repointScalars = async (
    cId: string,
    emails: string[],
    phones: string[]
  ): Promise<RepointOutcome> => {
    const email = pickScalarRepoint('email', contact?.email, emails);
    const phone = pickScalarRepoint('phone', contact?.phone, phones);
    if (!email && !phone) {
      return { attempted: false, ok: true };
    }
    try {
      const echoed = await contactsAPI.updateContact(cId, {
        ...(email ? { email } : {}),
        ...(phone ? { phone } : {}),
      });
      // Only report a rewrite: an echo identical to what we sent needs no
      // substitution downstream.
      return {
        attempted: true,
        ok: true,
        rewritten: {
          ...(email && echoed?.email && echoed.email !== email
            ? { email: { sent: email, stored: echoed.email } }
            : {}),
          ...(phone && echoed?.phone && echoed.phone !== phone
            ? { phone: { sent: phone, stored: echoed.phone } }
            : {}),
        },
      };
    } catch {
      return { attempted: true, ok: false };
    }
  };

  const submitEdit = async (values: ContactFormValues, emails: string[], phones: string[]) => {
    if (!contactId) {
      return;
    }
    const baseline = snapshotRef.current;
    const nameChanged = values.name.trim() !== baseline.name.trim();

    if (nameChanged) {
      try {
        await contactsAPI.updateContact(contactId, { name: values.name.trim() });
        queryClient.invalidateQueries({ queryKey: [contactsEndpoints.contact(contactId)] });
        queryClient.invalidateQueries({ queryKey: ['contacts/list'] });
      } catch {
        toast.error(t('contacts.update_failed'));
        return;
      }
    }

    // Runs before the identity writes: the contact write indexes the normalized
    // value as an identity, so adopting what it echoed back makes our upsert land
    // on that same row instead of adding a raw-text duplicate beside it.
    const repoint = await repointScalars(contactId, emails, phones);
    const rewrittenEmail = repoint.rewritten?.email;
    const rewrittenPhone = repoint.rewritten?.phone;
    const rows = {
      emails: rewrittenEmail
        ? withCanonicalRowValue(values.emails, rewrittenEmail.sent, rewrittenEmail.stored)
        : values.emails,
      phones: rewrittenPhone
        ? withCanonicalRowValue(values.phones, rewrittenPhone.sent, rewrittenPhone.stored)
        : values.phones,
    };

    const { upserts, deletes } = diffIdentities({ ...values, ...rows }, initialIdentities);
    const outcome = await runUpserts(contactId, upserts);

    // Only retire an old identity once its replacement upsert has landed.
    const replacementDeletes = upserts
      .map((op, index) =>
        op.replacesIdentityId && outcome.results[index]?.status === 'fulfilled'
          ? op.replacesIdentityId
          : null
      )
      .filter((id): id is string => id !== null);

    const deleteTargets = [...deletes, ...replacementDeletes];
    const deleteResults = await Promise.allSettled(
      deleteTargets.map((identityId) => deleteIdentity({ contactId, identityId }))
    );
    // A 404 means the row is already retired, which is exactly what was asked
    // for: the repoint above soft-deletes the identity matching the column's old
    // value, and that value is usually the row the user just removed.
    const deleteFailures = deleteResults.filter(
      (r) =>
        r.status === 'rejected' &&
        (r.reason as { statusCode?: number } | undefined)?.statusCode !== 404
    ).length;

    // A repoint moves the email/phone the contacts list and the detail view
    // render, so those queries must refetch. Identity rows on their own do not
    // feed those columns, but the contact query is refreshed on any identity
    // change too so every consumer of the contact row stays consistent.
    if (repoint.attempted || upserts.length > 0 || deleteTargets.length > 0) {
      queryClient.invalidateQueries({ queryKey: [contactsEndpoints.contact(contactId)] });
      queryClient.invalidateQueries({ queryKey: ['contacts/list'] });
    }

    const addedOrReplaced = outcome.results.some((r) => r.status === 'fulfilled');
    let consentOk = true;
    if (values.consent !== baseline.consent) {
      consentOk = (await writeWildcardConsent(contactId, values.consent ? 'granted' : 'revoked'))
        .ok;
    } else if (!values.consent && addedOrReplaced) {
      // The contact is opted out and we added or replaced an identity; the new row
      // must inherit the revoked wildcard consent instead of defaulting to allowed.
      consentOk = (await writeWildcardConsent(contactId, 'revoked')).ok;
    }

    // Same as create: a failed consent flip or identity write keeps the panel
    // open so it can be retried. Re-submitting re-diffs against the frozen
    // originals, and both the upserts and the consent writes are idempotent.
    if (reportOutcome(outcome, { deleteFailures, consentOk, repointOk: repoint.ok })) {
      toast.success(t('contacts.updated'));
      onClose();
    }
  };

  const onValidSubmit = async (values: ContactFormValues) => {
    if (!isConsentReady) {
      return;
    }
    const emails = cleanEmails(values.emails);
    const phones = cleanPhones(values.phones);
    const requiresContactMethod = !isEdit || !hasUneditableIdentity;
    if (requiresContactMethod && emails.length === 0 && phones.length === 0) {
      setContactMethodError(t('contacts.errors.email_or_phone_required'));
      return;
    }
    setContactMethodError(null);
    setIsSubmitting(true);
    try {
      if (isEdit) {
        await submitEdit(values, emails, phones);
      } else {
        await submitCreate(values, emails, phones);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitLabel = isEdit ? t('common.update') : t('common.add');

  return (
    <SidebarWrapper>
      <SidebarWrapperTitle className="pr-4">
        <h2 className="text-base leading-none font-medium text-foreground">
          {isEdit ? t('contacts.edit_contact') : t('contacts.add_contact')}
        </h2>
        <Button
          variant="ghost-neutral"
          size="icon-sm"
          type="button"
          onClick={handleClose}
          disabled={isSubmitting}
          aria-label={t('common.close')}
        >
          <XIcon size={16} className="text-muted-foreground" />
        </Button>
      </SidebarWrapperTitle>

      <Form {...form}>
        <SidebarWrapperForm onSubmit={form.handleSubmit(onValidSubmit)} noValidate>
          <SidebarWrapperContent className="px-1 pb-1">
            {/* Name */}
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem className="space-y-2">
                  <FormLabel>{t('contacts.name')}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder={t('contacts.name_placeholder')}
                      autoFocus
                      className={INVALID_INPUT}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Emails */}
            <div className="space-y-2">
              {emailsArray.fields.map((row, index) => (
                <FormField
                  key={row.id}
                  control={form.control}
                  name={`emails.${index}.value`}
                  render={({ field }) => (
                    <FormItem className="space-y-2">
                      <FormLabel>
                        {index === 0
                          ? t('contacts.email')
                          : `${t('contacts.email')} (${index + 1})`}
                      </FormLabel>
                      <div className="relative">
                        <FormControl>
                          <Input
                            {...field}
                            type="email"
                            placeholder={t('contacts.email_placeholder')}
                            className={cn(emailsArray.fields.length > 1 && 'pr-9', INVALID_INPUT)}
                          />
                        </FormControl>
                        {emailsArray.fields.length > 1 && (
                          <InlineRemoveButton
                            onClick={() => handleRemoveEmail(index)}
                            label={t('common.remove')}
                          />
                        )}
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ))}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleAddEmail}
                disabled={emailsArray.fields.length >= MAX_EMAILS}
              >
                <PlusIcon size={16} />
                {t('contacts.add_email')}
              </Button>
            </div>

            {/* Phones */}
            <div className="space-y-2">
              {phonesArray.fields.map((row, index) => (
                <FormField
                  key={row.id}
                  control={form.control}
                  name={`phones.${index}.value`}
                  render={({ field }) => (
                    <FormItem className="space-y-2">
                      <FormLabel>
                        {index === 0
                          ? t('contacts.phone')
                          : `${t('contacts.phone')} (${index + 1})`}
                      </FormLabel>
                      <div className="relative">
                        <FormControl>
                          <Input
                            {...field}
                            type="tel"
                            placeholder={t('contacts.phone_placeholder')}
                            className={cn(phonesArray.fields.length > 1 && 'pr-9', INVALID_INPUT)}
                          />
                        </FormControl>
                        {phonesArray.fields.length > 1 && (
                          <InlineRemoveButton
                            onClick={() => handleRemovePhone(index)}
                            label={t('common.remove')}
                          />
                        )}
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ))}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleAddPhone}
                disabled={phonesArray.fields.length >= MAX_PHONES}
              >
                <PlusIcon size={16} />
                {t('contacts.add_phone')}
              </Button>
              {contactMethodError && <p className="text-sm text-warning">{contactMethodError}</p>}
            </div>

            {/* Consent */}
            <div className="space-y-2">
              <FormField
                control={form.control}
                name="consent"
                render={({ field }) => (
                  <label
                    htmlFor="contact-consent"
                    className={cn(
                      'flex items-center gap-2 rounded-md border border-border p-4',
                      isConsentReady ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'
                    )}
                  >
                    <Checkbox
                      id="contact-consent"
                      checked={field.value}
                      disabled={!isConsentReady}
                      onCheckedChange={(checked) => field.onChange(checked === true)}
                    />
                    <span className="text-sm leading-5 font-medium text-foreground">
                      {t('contacts.consent_label')}
                    </span>
                  </label>
                )}
              />
              {isEdit && wildcardStatus === 'error' && (
                <p className="text-sm text-warning">{t('contacts.errors.consent_load_failed')}</p>
              )}
            </div>
          </SidebarWrapperContent>

          <SidebarWrapperFooter>
            <Button type="submit" isLoading={isSubmitting} disabled={!isConsentReady}>
              {submitLabel}
            </Button>
            <Button
              type="button"
              variant="ghost-neutral"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              {t('common.cancel')}
            </Button>
          </SidebarWrapperFooter>
        </SidebarWrapperForm>
      </Form>

      {isUnsavedOpen && (
        <UnsavedChangesModal
          onDiscard={() => {
            setIsUnsavedOpen(false);
            onClose();
          }}
          onGoBack={() => setIsUnsavedOpen(false)}
          onOpenChange={(open) => {
            if (!open) {
              setIsUnsavedOpen(false);
            }
          }}
        />
      )}
    </SidebarWrapper>
  );
};
